import { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { fromUnixTime, getUnixTime, max, subDays } from 'date-fns';
import { Tooltip, SkeletonLine, Button, IconButton, InfoBox, DateRangePicker } from '@crazyegginc/hatch';

import { DashHeader } from '/src/components/headers/DashHeader';
import { Breadcrumb, BreadcrumbItem } from '/src/components/Breadcrumb';
import { ActionButton } from '/src/features/goals/components/detail/ActionButton';
import { NotificationsButton } from '/src/features/notifications/components/NotificationsButton';
import { DashboardPage } from '/src/components/Page';
import { RenameModal } from '../components/modals/RenameModal';
import { SelectionProvider } from '/src/contexts/selection';
import { isSemrush } from '/src/utils/semrush';

import {
  usePermissions,
  useAuthContext,
  useCanonicalDateRangeParam,
  useSite,
  useNotifications,
  useModal,
  useSelection,
  useQueryParams,
  useTimeZonedFunctions,
} from '/src/hooks';

import { flashElement, removeHash, isSharedResourceSubscriptionError } from '/src/utils';
import { getFeatureName } from '/src/features/_global/utils';
import { NOTIFICATION_RESOURCE_TYPES } from '/src/features/notifications/constants';
import { combineTriggersWithSegments } from '/src/features/goals/utils';
import { goalSelectedDatesAtom } from '/src/features/goals/store';
import { FEATURES, MAX_ALLOWABLE_DATE_RANGES, TIMEZONES } from '/src/features/_global/constants';
import { MAXIMUM_SELECTION, FAKE_DELETED_TRIGGERS_ID } from '/src/features/goals/constants';
import { JSON_DATE_RANGE_ADAPTER } from '/src/utils/date-range-adapters';

import { goalDetailQuery, goalCountsQuery } from '/src/features/goals/queries';
import { abTestListPaginatedQuery } from '/src/features/ab-testing/queries';
import { SEO } from '/src/components/SEO';
import { SitePickerBar } from '/src/components/site-picker';
import { GoalDetailCard } from '/src/features/goals/components/detail/DetailCard';
import { PaginationControls } from '/src/components/PaginationControls';
import { DateDisplay } from '/src/components/date-time/DateDisplay';
import { GoalAbTestsTable } from '../components/detail/GoalAbTestsTable';
import { GoalPageConversionsTable } from '../components/detail/GoalPageConversionsTable';

import { ReactComponent as ArrowIcon } from '@crazyegginc/hatch/dist/images/icon-arrow.svg';
import { ReactComponent as EditIcon } from '@crazyegginc/hatch/dist/images/icon-pencil-filled.svg';
import { ReactComponent as PlusIcon } from '@crazyegginc/hatch/dist/images/icon-plus.svg';
import { AppLink } from '/src/features/_global/components/AppLink';

const PER_PAGE = 10;

function GoalDetail({ goal = null, isFetching = false, error = null }) {
  const { isSharing } = useAuthContext();
  const { addToSelection, currentSelection } = useSelection();
  const notifications = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [page, setPage] = useState(1);
  const permissions = usePermissions();
  const canVisitGoals = permissions.can('navigate', FEATURES.GOALS).allowed;
  const canViewAbTests = permissions.can('view', FEATURES.AB_TESTING).allowed;
  const [selection, setSelection] = useAtom(goalSelectedDatesAtom);
  const resetGoalSelectedDates = useResetAtom(goalSelectedDatesAtom);
  const { set: paramSet } = useQueryParams();
  const { startOfDay } = useTimeZonedFunctions();

  /*
    We only started inserting data into Clickhouse on Aug 16, 2023. No data before that exists in Clickhouse. 
    While some users have Goals that existed and received data prior to that date they aren’t in Clickhouse and 
    there is no easy way to import them. Since this is over 2 years ago we are limiting users from seeing data 
    before this timestamp. Eventually we’ll standardize data limits based on payment plan and other factors
  */
  const featureMinDate = startOfDay(new Date(2023, 8, 16));
  const goalCreatedAt = goal?.createdAt ?? null;
  const goalCreatedAtDate = goalCreatedAt ? startOfDay(fromUnixTime(goalCreatedAt)) : null;
  const featureStartedAt = useMemo(
    () => max([featureMinDate, goalCreatedAtDate].filter(Boolean)),
    [featureMinDate, goalCreatedAtDate],
  );
  const { resolvedDateRange } = useCanonicalDateRangeParam({
    featureStartedAt: featureStartedAt ?? undefined,
    maxAllowableRange: MAX_ALLOWABLE_DATE_RANGES.DEFAULT,
  });
  const normalizedSelection = useMemo(() => {
    if (resolvedDateRange?.selectedDateRange?.special) {
      return {
        special: resolvedDateRange.selectedDateRange.special,
        startDate: null,
        endDate: null,
      };
    }

    if (resolvedDateRange?.selectedDateRange?.start_date && resolvedDateRange?.selectedDateRange?.end_date) {
      return {
        special: null,
        startDate: getUnixTime(resolvedDateRange.selectedDateRange.start_date),
        endDate: getUnixTime(resolvedDateRange.selectedDateRange.end_date),
      };
    }

    return selection;
  }, [resolvedDateRange?.selectedDateRange, selection]);

  useEffect(() => {
    // reset in case there were any previously selected values.
    // it will still respect URL query params.
    resetGoalSelectedDates();
  }, [resetGoalSelectedDates]);

  const chooseDateRange = useCallback(
    (value) => {
      if (value.special) {
        paramSet('date', JSON_DATE_RANGE_ADAPTER.serialize(value));
        setSelection({ special: value.special, startDate: null, endDate: null });
      } else if (value.start_date && value.end_date) {
        const newStartDate = getUnixTime(value.start_date);
        const newEndDate = getUnixTime(value.end_date);
        paramSet('date', JSON_DATE_RANGE_ADAPTER.serialize(value));
        setSelection({ special: null, startDate: newStartDate, endDate: newEndDate });
      }
    },
    [paramSet, setSelection],
  );

  useEffect(() => {
    if (!resolvedDateRange?.selectedDateRange) {
      return;
    }

    if (JSON.stringify(normalizedSelection) !== JSON.stringify(selection)) {
      setSelection(normalizedSelection);
    }
  }, [normalizedSelection, resolvedDateRange?.selectedDateRange, selection, setSelection]);
  const abTestsCount = goal?.abTestsCount ?? 0;
  const convertedDateRange = resolvedDateRange?.convertedDateRange;

  const { data: countsData, isFetching: fetchingCounts } = useQuery({
    ...goalCountsQuery({
      id: Number(id),
      startDate: convertedDateRange.startDate,
      endDate: convertedDateRange.endDate,
    }),
    networkMode: 'always',
    cacheTime: 0,
    keepPreviousData: false,
    onSuccess(goalCountsData) {
      if (!currentSelection || currentSelection.length === 0) {
        const visibleTriggers = combineTriggersWithSegments(goal, goalCountsData.goalCounts.triggers);

        const maxSelectable = Math.min(MAXIMUM_SELECTION, visibleTriggers.length);

        setTimeout(() => {
          addToSelection([...visibleTriggers.slice(0, maxSelectable)]);
        }, 100);
      }
    },
  });

  const { data: abTestsData, isFetching: fetchingAbTests } = useQuery({
    ...abTestListPaginatedQuery({
      goalId: goal?.id,
      page,
      limit: PER_PAGE,
      presetDate: normalizedSelection.special,
      startDate: normalizedSelection.startDate,
      endDate: normalizedSelection.endDate,
    }),
    enabled: canViewAbTests && Boolean(goal?.id) && abTestsCount > 0,
    keepPreviousData: true,
  });

  useEffect(() => {
    if (error && error.message.match(/Goal not found/)) {
      notifications.error({
        id: 'goal-not-found',
        content: `No matching goal found`,
        timeout: 3000,
        skipHoneybadger: true,
      });
      navigate(canVisitGoals ? '/goals' : '/not-found', { replace: true });
    }
  }, [error, navigate, notifications, canVisitGoals]);

  const canViewGoal = permissions.can('view', FEATURES.GOALS).allowed;

  useEffect(() => {
    if (goal && !canViewGoal) {
      notifications.error({
        id: 'goal-view-permission-denied',
        content: 'You do not have permission to view that goal.',
        timeout: 3000,
        skipHoneybadger: true,
      });
      navigate('/goals', { replace: true });
    }
  }, [canViewGoal, goal, navigate, notifications]);

  const counts = countsData?.goalCounts ?? null;
  const abTests = abTestsData?.abTestListPaginated?.list ?? [];
  const showABTestTable = canViewAbTests && abTestsCount > 0;

  useEffect(() => {
    if (showABTestTable && location.hash === '#ab-tests') {
      setTimeout(() => {
        const el = document.querySelector('#ab-tests > div');
        flashElement(el);
        removeHash();
      }, 1000);
    }
  }, [showABTestTable, location.hash]);

  return (
    <div className="mt-10 flex w-full flex-col px-10">
      <div className="mb-3.75 flex items-end justify-between">
        {fetchingCounts ? (
          <SkeletonLine width="35%" height="10px" className="!rounded-sm" />
        ) : (
          <h2 className="text-sm text-lynch-500 leading-none flex flex-1 whitespace-pre">
            Conversion created: {goal?.createdAt ? <DateDisplay date={goal?.createdAt} full={true} /> : 'N/A'}
            {goal?.createdBy ? ` by ${goal.createdBy?.name ? goal.createdBy.name : goal.createdBy.login}` : null}
          </h2>
        )}

        <div className="flex">
          {!isSharing && !isSemrush && (
            <div className=" mr-3.75">
              <NotificationsButton
                resourceType={NOTIFICATION_RESOURCE_TYPES.GOAL}
                resourceId={goal?.id}
                variant="ghost-primary"
              />
            </div>
          )}

          {goal?.createdAt ? (
            <DateRangePicker
              startDate={resolvedDateRange?.pickerProps.startDate}
              endDate={resolvedDateRange?.pickerProps.endDate}
              lowerBoundary={resolvedDateRange?.pickerProps.lowerBoundary}
              upperBoundary={resolvedDateRange?.pickerProps.upperBoundary}
              size="base"
              special={resolvedDateRange?.pickerProps.special}
              tooltipOptions={{}}
              setDateRange={chooseDateRange}
              setCommonDateRange={chooseDateRange}
              showCommonRanges={true}
              timezone={TIMEZONES.UTC}
              showTimeZone={true}
              maxRange={resolvedDateRange?.pickerProps.maxRange}
            />
          ) : null}
        </div>
      </div>

      <GoalDetailCard
        goal={goal}
        isFetching={isFetching}
        abTestsCount={abTestsCount}
        counts={counts}
        isFetchingCounts={fetchingCounts}
      />

      <GoalPageConversionsTable goal={goal} />

      {showABTestTable ? (
        <>
          <div className="mb-5 mt-10 flex items-center">
            <h2 className="text-header-3 mr-4">A/B Tests using this Conversion</h2>
            <InfoBox
              header="Why don't A/B Test conversions match Total conversions?"
              body="One visitor may successfully complete this Conversion on multiple tests, however they will only count for one conversion under the total conversion count."
            />
          </div>
          <GoalAbTestsTable loading={isFetching || fetchingAbTests} abTests={abTests} />
          {abTestsCount > PER_PAGE && (
            <div className="mt-4">
              <PaginationControls
                currentPage={page}
                perPage={PER_PAGE}
                total={abTestsCount}
                setPage={(page) => setPage(page)}
              />
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

function GoalDetailsPageContent() {
  const { isSharing, sharedResource } = useAuthContext();
  const { id } = useParams();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const canVisitDashboard = permissions.can('navigate', FEATURES.GOALS).allowed;
  const canEditGoals = permissions.can('edit', FEATURES.GOALS).allowed;
  const canCreateGoals = permissions.can('create', FEATURES.GOALS).allowed;
  const modal = useModal();
  const [selection] = useAtom(goalSelectedDatesAtom);

  const { sites, loadingSites, selectSite } = useSite({
    dontSelectSite: true,
    sitesQueryProps: { enabled: !isSharing },
  });

  const { data, isFetching, error, isInitialLoading } = useQuery({
    ...goalDetailQuery({
      id: Number(id),
      presetDate: selection.special,
      startDate: selection.startDate,
      endDate: selection.endDate,
    }),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (data?.goalDetail?.siteId) {
      selectSite(data.goalDetail.siteId);
    }
  }, [data, selectSite]);

  const goal = useMemo(() => {
    if (!data?.goalDetail) return null;

    // fake trigger where we accumulate all previously deleted triggers
    const fakeTrigger = {
      id: FAKE_DELETED_TRIGGERS_ID,
      type: 'other',
      name: 'deleted triggers from the past',
      autoName: 'deleted triggers from the past',
      filter: '{}',
      createdAt: getUnixTime(subDays(new Date(), 110)),
      deletedAt: getUnixTime(subDays(new Date(), 100)),
    };
    return {
      ...data.goalDetail,
      triggers: [...(data.goalDetail.triggers ?? []), fakeTrigger],
    };
  }, [data?.goalDetail]);

  const pickerSite = isSharing
    ? { name: sharedResource?.resource?.siteName, id: sharedResource?.resource?.siteId }
    : sites?.find((s) => s.id === goal?.siteId);

  if (error && isSharedResourceSubscriptionError(error)) {
    return <Navigate replace to="/not-found" />;
  }

  return (
    <DashboardPage>
      <SEO title={getFeatureName(FEATURES.GOALS)} />
      <DashHeader
        titleComponent={
          <Breadcrumb>
            {!isSharing && (
              <BreadcrumbItem active={false} to="/goals">
                Conversions
              </BreadcrumbItem>
            )}
            <BreadcrumbItem active={true}>
              <div className="mr-5 flex items-center space-x-1">
                {goal?.name ? (
                  <>
                    <span className="truncate" data-testid="goal-name">
                      {goal.name}
                    </span>
                    {canEditGoals ? (
                      <Tooltip placement="bottom" tooltipContent="Rename Goal">
                        <IconButton
                          icon={<EditIcon className="h-4 w-4 fill-current" />}
                          className="h-full shrink-0 text-malibu-500 hover:text-dodger-blue-500"
                          onClick={() => {
                            modal.show(<RenameModal goal={goal} />);
                          }}
                          label="edit Goal name"
                        />
                      </Tooltip>
                    ) : null}
                  </>
                ) : isInitialLoading ? (
                  <SkeletonLine width="300px" height="16px" />
                ) : null}
              </div>
            </BreadcrumbItem>
          </Breadcrumb>
        }
        actionButton={
          <div className="flex flex-shrink-0 items-center space-x-2">
            {canVisitDashboard ? (
              <Button
                component={AppLink}
                variant="ghost-primary"
                to={{
                  pathname: `/goals`,
                }}
                size="xl"
                className="text-center leading-none"
              >
                <ArrowIcon className="mr-2 h-3 w-3 shrink-0 rotate-180 fill-current" />
                Back to all Conversions
              </Button>
            ) : null}
            {canEditGoals ? <ActionButton goal={goal} redirectOnDelete="/goals" variant="primary" /> : null}
            {canCreateGoals ? (
              <Button
                size="xl"
                onClick={() => {
                  navigate('/goals/new');
                }}
                leftIcon={<PlusIcon className="mr-2 h-2.5 w-2.5 fill-current" />}
              >
                Track New Conversions
              </Button>
            ) : null}
          </div>
        }
      />
      <SitePickerBar
        sites={pickerSite ? [pickerSite] : []}
        selectedSite={pickerSite}
        loading={isFetching || (loadingSites && !isSharing)}
        pageDisplayName="Goal Details"
      />

      {goal && <GoalDetail goal={goal} isFetching={isFetching} error={error} />}
    </DashboardPage>
  );
}

// eslint-disable-next-line no-restricted-syntax
export default function GoalDetailsPage() {
  return (
    <SelectionProvider>
      <GoalDetailsPageContent />
    </SelectionProvider>
  );
}
